import type { LintFinding } from '@ralph/contracts'
import { useId } from 'react'
import { BuilderSection, SECTION_IDS } from '@/components/marketplace/maker/BuilderShell.tsx'
import type { BuilderDraft } from '@/components/marketplace/maker/builder-form.ts'
import { findingsFor, LintNotes } from '@/components/marketplace/maker/LintNotes.tsx'
import { INSTRUCTIONS_MAX } from '@/components/marketplace/maker/maker-limits.ts'
import { Textarea } from '@/components/ui/primitives.tsx'
import { useI18n } from '@/i18n/index.ts'

/**
 * One large field, and the guidance beside it rather than behind a tooltip.
 *
 * The guidance list is static prose on purpose: it is the difference between a maker who
 * writes "be helpful" and one who writes what to do when the answer is not in the
 * documents, and a hint that has to be hovered to be read may as well not exist.
 *
 * // WIRE: AGM-023 also asks for optional AI-assisted drafting whose output is an editable
 * // draft that is never auto-saved and never auto-published. That needs a maker-plane
 * // drafting endpoint (nothing in the memo's route map covers it), so it is deliberately
 * // absent here rather than faked — the field below is the whole surface for now.
 */
export const InstructionsSection = ({
  step,
  draft,
  disabled,
  lint,
  onChange
}: {
  step: number
  draft: BuilderDraft
  disabled: boolean
  lint: LintFinding[]
  onChange: (patch: Partial<BuilderDraft>) => void
}) => {
  const i18n = useI18n()
  // In the body, never at module scope (PRD-27 F-3).
  const strings = i18n.t.marketplace.maker.sections.instructions
  const fieldId = useId()

  return (
    <BuilderSection id={SECTION_IDS.instructions} step={step} title={strings.title} lead={strings.lead}>
      <div className="grid gap-3.5 lg:grid-cols-[minmax(0,1fr)_15.5rem]">
        <div className="min-w-0">
          <div className="mb-1.5 flex items-baseline justify-between gap-2">
            <label htmlFor={`${fieldId}-instructions`} className="eyebrow text-fg-muted">
              {strings.label}
            </label>
            {/* A measured number, so it is set in the same tabular mono as every other
                measured number in the product. */}
            <span className="readout text-fg-subtle">{strings.counted(i18n, draft.instructions.length)}</span>
          </div>
          <Textarea
            id={`${fieldId}-instructions`}
            rows={14}
            value={draft.instructions}
            maxLength={INSTRUCTIONS_MAX}
            disabled={disabled}
            placeholder={strings.placeholder}
            onChange={(event) => onChange({ instructions: event.target.value })}
          />
          <p className="mt-1.5 text-pretty text-[12.5px] leading-[1.5] text-fg-subtle">{strings.hint}</p>
        </div>

        <aside className="rounded-tile border border-rule bg-card-solid px-3.5 py-3">
          <ul className="space-y-2.5">
            {strings.guidance.map((line) => (
              <li key={line} className="flex gap-2 text-pretty text-[12.5px] leading-[1.5] text-fg-muted">
                <span aria-hidden className="mt-[7px] h-[3px] w-[3px] shrink-0 rounded-full bg-primary" />
                <span className="min-w-0">{line}</span>
              </li>
            ))}
          </ul>
        </aside>
      </div>

      <LintNotes findings={findingsFor(lint, 'instructions')} />
    </BuilderSection>
  )
}
